// src/api/listedproducts.js
import { apiGet, apiPut, apiPost, extractMessage } from "../client";

const METHOD_PATH = "/api/method/dynesourcerapi.api.get_mortgage_products";
const DELETE_PRODUCT_PATH =
  "/api/method/dynesourcerapi.product_lifecycle.delete_product";
const DOCTYPE = "Mortgage Product";
// DOCTYPE_PATH will be: /api/resource/Mortgage%20Product
// Full update path: /api/resource/Mortgage%20Product/{docname}
// Example: /api/resource/Mortgage%20Product/BTL-003716
const DOCTYPE_PATH = `/api/resource/${encodeURIComponent(DOCTYPE)}`;

/** Soft-deleted while attached to an ongoing application — hide from lender portal. */
function isHiddenFromLenderPortal(row) {
  const status = String(row?.record_status ?? "").trim().toLowerCase();
  return status === "on_going_app";
}

/**
 * Delete product via product lifecycle API.
 * Hard-deletes when unused; soft-deletes (record_status: on_going_app) when
 * attached to an ongoing application — either way product leaves the portal UI.
 *
 * Backend requires product_type as the product line category (BTL, IF, …).
 */
async function deleteProductLifecycle({ name, doctype, product_type }) {
  const docname = String(name || "").trim();
  const productType = String(product_type || "").trim();
  if (!docname) throw new Error("Product name is required.");
  if (!doctype) throw new Error("Product doctype is required.");
  if (!productType) throw new Error("Product type is required.");

  const res = await apiPost(DELETE_PRODUCT_PATH, {
    name: docname,
    doctype,
    product_type: productType,
  });
  const message = extractMessage(res);

  if (message && typeof message === "object" && message.success === false) {
    throw new Error(
      message.error || message.message || "Failed to delete product."
    );
  }
  return message;
}

/* ---------- helper: normalise payload to rows array ---------- */
function toArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.products)) return payload.products;
  if (Array.isArray(payload.result)) return payload.result;

  if (typeof payload === "object") {
    const vals = Object.values(payload);
    if (
      vals.length &&
      vals.every((v) => typeof v === "object" && !Array.isArray(v))
    ) {
      return vals;
    }
  }
  return [];
}

/* ---------- GET products ---------- */
export async function getMortgageProducts(params = {}) {
  const data = await apiGet(METHOD_PATH, params);
  const peeled = extractMessage(data);
  const rows = toArray(peeled).filter((row) => !isHiddenFromLenderPortal(row));
  return rows.map(normalizeRow);
}

const ALL_PAGES_BATCH_SIZE = 200;
const ALL_PAGES_MAX_REQUESTS = 200;

/**
 * Page through a list endpoint until it stops returning new rows.
 * The backend caps each response (200 rows), so one large limit is not enough.
 * Sends both limit_start/limit_page_length and page/limit so either paging style works.
 */
async function fetchAllPages(path, params = {}, { pageParams = true } = {}) {
  const { limit, page, page_length, limit_start, limit_page_length, ...rest } = params;
  const byName = new Map();
  const unnamed = [];

  for (let i = 0; i < ALL_PAGES_MAX_REQUESTS; i += 1) {
    const data = await apiGet(path, {
      ...rest,
      limit_start: i * ALL_PAGES_BATCH_SIZE,
      limit_page_length: ALL_PAGES_BATCH_SIZE,
      ...(pageParams ? { page: i + 1, limit: ALL_PAGES_BATCH_SIZE } : {}),
    });
    const batch = toArray(extractMessage(data));
    let added = 0;
    batch.forEach((row) => {
      const key = String(row?.name ?? "").trim();
      if (!key) {
        unnamed.push(row);
        added += 1;
      } else if (!byName.has(key)) {
        byName.set(key, row);
        added += 1;
      }
    });
    if (batch.length === 0 || added === 0) break;
  }

  return [...byName.values(), ...unnamed];
}

/** All BTL/Mortgage products matching params, across every backend page. */
export async function getAllMortgageProducts(params = {}) {
  const rows = await fetchAllPages(METHOD_PATH, params);
  return rows.filter((row) => !isHiddenFromLenderPortal(row)).map(normalizeRow);
}

/* ---------- GET products with advanced filters (get_mortgage_products_details) ---------- */
const ADVANCED_METHOD_PATH = "/api/method/dynesourcerapi.api.get_mortgage_products_details";

export async function getMortgageProductsDetails(params = {}) {
  const data = await apiGet(ADVANCED_METHOD_PATH, params);
  const peeled = extractMessage(data);
  const rows = toArray(peeled).filter((row) => !isHiddenFromLenderPortal(row));
  return rows.map(normalizeRow);
}

/**
 * Frappe Table / Table MultiSelect fields. Sending a string (e.g. "Fixed")
 * makes doc.extend iterate characters and throw:
 * TypeError: 'str' object does not support item assignment
 */
const MORTGAGE_TABLE_FIELDS = new Set([
  "product_type",
  "loan_purpose",
  "borrower_type",
  "tenancy_type",
  "credit_worthiness",
  "btl_experince_year",
  "deposit_originantion",
  "home_owner",
  "fh_or_lh",
]);

function isChildRowArray(value) {
  return (
    Array.isArray(value) &&
    value.every((row) => row && typeof row === "object" && !Array.isArray(row))
  );
}

/** Keep only scalar fields and real child-table row arrays for Frappe PUT. */
function sanitizeFrappeUpdatePayload(payload, tableFields = MORTGAGE_TABLE_FIELDS) {
  const out = {};
  for (const [key, value] of Object.entries(payload || {})) {
    if (!key || key.startsWith("_") || key === "doctype") continue;
    if (value === undefined) continue;
    if (tableFields.has(key)) {
      if (isChildRowArray(value)) out[key] = value;
      continue;
    }
    out[key] = value;
  }
  return out;
}

/* ---------- UPDATE product (PUT) ---------- */
export async function updateMortgageProduct(docname, payload) {
  // Construct the path: /api/resource/Mortgage Product/{docname}
  // Example: /api/resource/Mortgage%20Product/BTL-003716
  const path = `${DOCTYPE_PATH}/${encodeURIComponent(docname)}`;
  const data = sanitizeFrappeUpdatePayload(payload);

  console.log("🔄 updateMortgageProduct called:");
  console.log("  - Docname:", docname);
  console.log("  - Full path:", path);
  console.log("  - Payload:", data);

  // PUT fields at the top level — same shape as create / lender profile updates.
  const res = await apiPut(path, data);

  console.log("✅ updateMortgageProduct response:", res);
  return extractMessage(res);
}

/* ---------- DELETE product ---------- */
export async function deleteMortgageProduct(docname) {
  return deleteProductLifecycle({
    name: docname,
    doctype: DOCTYPE,
    product_type: "BTL",
  });
}

export const LISTED_PRODUCT_LINES = [
  { value: "btl", label: "BTL", apiCategory: "BTL" },
  { value: "if", label: "Invoice Finance", apiCategory: "IF" },
];

function productDocId(row) {
  return String(row?._id || row?._raw?.name || row?.name || "").trim().toUpperCase();
}

export function isInvoiceFinanceProduct(row) {
  if (row?._raw?.doctype === "Invoice Finance") return true;
  const cat = String(row?.category || row?._raw?.product_category || "").trim().toLowerCase();
  if (cat === "if" || cat.includes("invoice")) return true;
  const id = productDocId(row);
  return id.startsWith("IF-");
}

/** True for buy-to-let mortgage rows, including Complex BTL (excludes invoice finance). */
export function isBtlMortgageProduct(row) {
  if (isInvoiceFinanceProduct(row)) return false;
  const cat = String(row?.category || row?._raw?.product_category || "").trim().toLowerCase();
  const id = productDocId(row);
  if (id.startsWith("BTL-")) return true;
  return cat === "btl" || cat === "cbtl" || cat === "" || cat === "—";
}

const INVOICE_FINANCE_DOCTYPE = "Invoice Finance";
/** Base: /api/resource/Invoice Finance */
const INVOICE_FINANCE_RESOURCE = `/api/resource/${encodeURIComponent(INVOICE_FINANCE_DOCTYPE)}`;

/**
 * Frappe document name for Invoice Finance (e.g. IF-00049).
 * Used as {sample_name} in GET/PUT/DELETE /api/resource/Invoice Finance/{sample_name}
 */
export function resolveInvoiceFinanceDocname(rowOrId) {
  if (rowOrId == null) return "";
  if (typeof rowOrId === "object") {
    return String(rowOrId._raw?.name || rowOrId._id || rowOrId.name || "").trim();
  }
  return String(rowOrId).trim();
}

function invoiceFinanceDocPath(docname) {
  const name = resolveInvoiceFinanceDocname(docname);
  if (!name) {
    throw new Error("Invoice Finance product ID is required (e.g. IF-00049).");
  }
  return `${INVOICE_FINANCE_RESOURCE}/${encodeURIComponent(name)}`;
}

const INVOICE_FINANCE_LIST_FIELDS = JSON.stringify([
  "name",
  "sector",
  "status",
  "record_status",
  "min_limit",
  "max_limit",
  "discount_rate_percent",
  "service_fee_percent",
  "min_service_fee",
  "contract_term_months",
  "ip_percent",
  "selective_invoicing",
  "part_full_fund_ledger",
  "owner",
]);

function buildInvoiceFinanceListQuery(params = {}) {
  const {
    product_name__like,
    name,
    status,
    sector,
    limit_page_length,
    limit_start,
    ...rest
  } = params;

  const filters = [];
  if (status && status !== "ALL") {
    filters.push(["status", "=", status]);
  }
  if (name) {
    filters.push(["name", "=", name]);
  }
  if (sector && sector !== "ALL") {
    filters.push(["sector", "=", sector]);
  }
  if (product_name__like) {
    filters.push(["sector", "like", `%${product_name__like}%`]);
  }

  const query = {
    fields: INVOICE_FINANCE_LIST_FIELDS,
    limit_page_length: limit_page_length ?? 1000,
    ...rest,
  };

  if (filters.length > 0) {
    query.filters = JSON.stringify(filters);
  }
  if (limit_start != null) {
    query.limit_start = limit_start;
  }

  return query;
}

/**
 * List Invoice Finance products — GET /api/resource/Invoice Finance
 */
export async function getInvoiceFinanceProducts(params = {}) {
  try {
    const data = await apiGet(
      INVOICE_FINANCE_RESOURCE,
      buildInvoiceFinanceListQuery(params)
    );
    const peeled = extractMessage(data);
    // Frappe resource list is usually { data: [...] }; extractMessage may already unwrap it.
    const rows = toArray(peeled).filter((row) => !isHiddenFromLenderPortal(row));
    return rows.map(normalizeInvoiceFinanceRow);
  } catch (err) {
    console.error("getInvoiceFinanceProducts failed:", err);
    throw err;
  }
}

/** All Invoice Finance products matching params, across every page. */
export async function getAllInvoiceFinanceProducts(params = {}) {
  const rows = await fetchAllPages(
    INVOICE_FINANCE_RESOURCE,
    buildInvoiceFinanceListQuery(params),
    { pageParams: false }
  );
  return rows.filter((row) => !isHiddenFromLenderPortal(row)).map(normalizeInvoiceFinanceRow);
}

/**
 * Single Invoice Finance product — GET /api/resource/Invoice Finance/{sample_name}
 */
export async function getInvoiceFinanceProduct(docname) {
  const path = invoiceFinanceDocPath(docname);
  const data = await apiGet(path);
  const row = extractMessage(data);
  if (!row || typeof row !== "object") {
    throw new Error("Invoice Finance product not found.");
  }
  return normalizeInvoiceFinanceRow(row);
}

/**
 * Update Invoice Finance product — PUT /api/resource/Invoice Finance/{sample_name}
 * @param {string|Object} docnameOrRow - Product ID (e.g. IF-00049) or normalized row
 */
export async function updateInvoiceFinanceProduct(docnameOrRow, payload) {
  const path = invoiceFinanceDocPath(docnameOrRow);
  const data = sanitizeFrappeUpdatePayload(payload, new Set());
  const res = await apiPut(path, data);
  return extractMessage(res);
}

/**
 * Delete Invoice Finance product via product lifecycle API.
 * Soft-deletes (on_going_app) when linked to an ongoing application; hard-deletes otherwise.
 * @param {string|Object} docnameOrRow - Product ID (e.g. IF-00049) or normalized row
 */
export async function deleteInvoiceFinanceProduct(docnameOrRow) {
  return deleteProductLifecycle({
    name: resolveInvoiceFinanceDocname(docnameOrRow),
    doctype: INVOICE_FINANCE_DOCTYPE,
    product_type: "IF",
  });
}

function fmtMoney(n) {
  if (n == null || isNaN(+n)) return "—";
  const num = Number(n);

  if (num >= 1_000_000) {
    const millions = num / 1_000_000;
    const decimals = millions % 1 === 0 ? 0 : 1;
    return `£${millions.toFixed(decimals)}m`;
  }
  if (num >= 1_000) {
    return `£${(num / 1_000).toFixed(0)}k`;
  }
  return `£${num.toLocaleString()}`;
}

/** Round and trim float noise for percent display. */
function formatPercentDisplay(num, decimalPlaces = 4) {
  const factor = 10 ** decimalPlaces;
  const rounded = Math.round(Number(num) * factor) / factor;
  if (isNaN(rounded)) return "—";
  if (Number.isInteger(rounded)) return `${rounded}%`;
  return `${rounded.toFixed(decimalPlaces).replace(/\.?0+$/, "")}%`;
}

/** Display backend percent fields as-is (no scale conversion), trimming float noise only. */
function formatBackendPercent(val, maxDecimals = 6) {
  if (val == null || val === "") return "—";
  const n = Number(val);
  if (isNaN(n)) return String(val).includes("%") ? String(val) : `${String(val)}%`;
  return formatPercentDisplay(n, maxDecimals);
}

function formatIfPercent(val) {
  return formatBackendPercent(val, 4);
}

function formatServiceFeePercent(val) {
  return formatBackendPercent(val, 8);
}

function formatMinServiceFee(val) {
  if (val == null || val === "") return "—";
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return fmtMoney(n);
}

/** contract_term_months (e.g. 24) for display */
function formatContractTermMonths(months) {
  if (months == null || months === "") return "—";
  const m = Number(months);
  if (isNaN(m)) return "—";
  return String(m);
}

function deriveIfProductType(row) {
  if (row?.product_type) return row.product_type;
  if (String(row?.selective_invoicing || "").toLowerCase() === "yes") {
    return "Selective";
  }
  if (String(row?.part_full_fund_ledger || "").toLowerCase() === "yes") {
    return "Whole ledger";
  }
  if (String(row?.part_full_fund_ledger || "").toLowerCase() === "no") {
    return "Partial ledger";
  }
  return "—";
}

/** Map Invoice Finance API status to UI labels (Active | Deactivated | Scheduled). */
function normalizeInvoiceFinanceStatus(row) {
  const disabled = row?.disabled;
  if (disabled === 1 || disabled === true || String(disabled).toLowerCase() === "yes") {
    return "Deactivated";
  }

  const raw = row?.status;
  const s = String(raw ?? "").trim();
  if (!s) return "Active";

  const lower = s.toLowerCase();
  if (["active", "activate", "live", "approved", "enabled"].includes(lower)) {
    return "Active";
  }
  if (["deactivated", "deactivate", "inactive", "disabled"].includes(lower)) {
    return "Deactivated";
  }
  if (["scheduled", "schedule"].includes(lower)) {
    return "Scheduled";
  }
  return s;
}

function normalizeInvoiceFinanceRow(row) {
  const docname = String(row.name || row.docname || row._id || row.id || "").trim();
  const sector = row.sector || "—";
  const minLimit = row.min_limit ?? null;
  const maxLimit = row.max_limit ?? null;
  const discountRateRaw = row.discount_rate_percent ?? null;
  const serviceFeeRaw = row.service_fee_percent ?? null;
  const minServiceFeeRaw = row.min_service_fee ?? null;
  const termMonths = row.contract_term_months ?? null;
  const ipPercentRaw = row.ip_percent ?? null;
  const productType = deriveIfProductType(row);

  return {
    _id: docname,
    productId: docname,
    product: sector,
    sector,
    ip: formatIfPercent(ipPercentRaw),
    serviceFee: formatServiceFeePercent(serviceFeeRaw),
    miniServiceFee: formatMinServiceFee(minServiceFeeRaw),
    discountRate: formatIfPercent(discountRateRaw),
    ifCategory: productType,
    category: "IF",
    type: productType,
    feeType: "",
    rate: "—",
    arrangementFee: "—",
    term: formatContractTermMonths(termMonths),
    contractTerm: formatContractTermMonths(termMonths),
    range:
      minLimit == null || maxLimit == null
        ? "—"
        : `${fmtMoney(minLimit)} - ${fmtMoney(maxLimit)}`,
    status: normalizeInvoiceFinanceStatus(row),
    lender_product_code: docname,
    expiryDate: null,
    product_schedule: row.product_schedule,
    date_time: row.date_time,
    _rateNum: null,
    _termYears: termMonths != null ? Number(termMonths) / 12 : null,
    _termMonths: Number(termMonths) || null,
    _arrangePct: null,
    _discountRateNum: Number(discountRateRaw) || null,
    _ipNum: Number(ipPercentRaw) || null,
    _serviceFeeNum: Number(serviceFeeRaw) || null,
    _miniServiceFeeNum: Number(minServiceFeeRaw) || null,
    _minLoan: Number(minLimit) || null,
    _maxLoan: Number(maxLimit) || null,
    _raw: row,
  };
}

/* ---------- Convert backend row → frontend row ---------- */
function normalizeRow(row) {
  const docname = row.name || row.docname || row._id || row.id;

  const productName = row.product_name || "—";

  // Category from backend (BTL / IF / SC / AF)
  const category = row.product_category || row.category || null;

  const productType = row.product_type || null; // Variable / Fixed / Discounted etc.

  const rateVal = row.product_rate_percent ?? null;
  const termYears = row.mortgage_term_years ?? null;
  const arrangementFeeAmt = row.product_fee_amount ?? null;

  const minLoan = row.min_loan ?? null;
  const maxLoan = row.max_loan ?? null;

  const feeType = (row.product_fee_type || "").trim(); // Pound | Percentage | Mini Fee

  // 🔧 MONEY FORMATTER – fixed to show 4.5m, 1.2m etc instead of rounding to 5m
  const fmtMoney = (n) => {
    if (n == null || isNaN(+n)) return "—";
    const num = Number(n);

    if (num >= 1_000_000) {
      const millions = num / 1_000_000;
      const decimals = millions % 1 === 0 ? 0 : 1; // whole → 4m, else 4.5m
      return `£${millions.toFixed(decimals)}m`;
    }
    if (num >= 1_000) {
      return `£${(num / 1_000).toFixed(0)}k`;
    }
    return `£${num.toLocaleString()}`;
  };

  // Arrangement fee display
  let arrangementFeeDisplay = "—";

  if (arrangementFeeAmt != null && !isNaN(arrangementFeeAmt)) {
    const num = Number(arrangementFeeAmt);

    switch (feeType) {
      case "Pound":
        arrangementFeeDisplay = `£${num.toLocaleString()}`;
        break;
      case "Percentage":
        arrangementFeeDisplay = `${num}%`;
        break;
      case "Mini Fee":
        arrangementFeeDisplay = `Min £${num.toLocaleString()}`;
        break;
      default:
        arrangementFeeDisplay = `£${num.toLocaleString()}`;
        break;
    }
  }

  // For Arr. Fee % filters, only when fee type is percentage
  const arrangeFilterValue =
    feeType === "Percentage" ? Number(arrangementFeeAmt) || null : null;

  const hasScheduleFlag =
    row.product_schedule === true ||
    String(row.product_schedule || "").toLowerCase() === "true" ||
    String(row.product_schedule || "") === "1";
  const hasScheduleDate = !!String(row.date_time || "").trim();
  const isScheduled = hasScheduleFlag && hasScheduleDate;

  const mappedStatus = isScheduled ? "Scheduled" : (row.status || "Active");
  const expiryDate =
    row.product_expiray_date ||
    row.product_expiry_date ||
    row.expiry_date ||
    null;

  const sector =
    row.sector ||
    row.sector_name ||
    productName ||
    "—";
  const serviceFeeRaw = row.service_fee ?? row.service_fee_percent ?? null;
  const miniServiceFeeRaw = row.mini_service_fee ?? row.mini_service_fee_percent ?? null;
  const discountRateRaw =
    row.discount_rate ?? row.discount_rate_percent ?? rateVal ?? null;

  return {
    _id: docname,
    product: productName,
    sector,
    serviceFee: formatIfPercent(serviceFeeRaw),
    miniServiceFee: formatIfPercent(miniServiceFeeRaw),
    discountRate: formatIfPercent(discountRateRaw),
    ifCategory: productType || "—",

    category: category || "—", // used by JSX "CATEGORY" column
    type: productType || "—", // original product_type (Variable/Fixed/etc.)

    feeType,
    rate: rateVal == null ? "—" : `${rateVal}%`,
    arrangementFee: arrangementFeeDisplay,

    term: termYears == null ? "—" : `${termYears} yrs`,

    range:
      minLoan == null || maxLoan == null
        ? "—"
        : `${fmtMoney(minLoan)} - ${fmtMoney(maxLoan)}`,

    status: mappedStatus,
    lender_product_code: row.lender_product_code || row.lenderProductCode || null,
    expiryDate,
    product_schedule: row.product_schedule,
    date_time: row.date_time,

    _rateNum: Number(rateVal) || null,
    _termYears: Number(termYears) || null,
    _arrangePct: arrangeFilterValue,
    _discountRateNum: Number(discountRateRaw) || null,
    _serviceFeeNum: Number(serviceFeeRaw) || null,
    _miniServiceFeeNum: Number(miniServiceFeeRaw) || null,
    _minLoan: Number(minLoan) || null,
    _maxLoan: Number(maxLoan) || null,

    _raw: row,
  };
}
